import { useMemo, useState } from "react"
import { useParams, useSearchParams } from "react-router-dom"
import AssetInformation from "../components/asset/AssetInformation"
import AssetFigures from "../components/asset/AssetFigures"
import { Section, SectionRoot } from "@hydrologiq/shared/chakra/custom/Section"
import AssetGraphics from "../components/asset/AssetGraphics"
import { Tabs, Text, VStack } from "@chakra-ui/react"
import { PaginatedTable } from "@hydrologiq/shared/components/PaginatedTable"
import { useAsset } from "../hooks/useAsset"
import DeploymentsTable from "@hydrologiq/shared/components/DeploymentsTable"
import { useAssetDeployments } from "../hooks/useAssetDeployments"
import { useIsSectionEnabled } from "@hydrologiq/shared/providers/SectionFeatures"
import { useCopyValues } from "@hydrologiq/shared/providers/helpers/useCopy"
import { COPY_KEYS, DEFAULT_COPY_MAPPINGS } from "@hydrologiq/shared/copy/constants"
import AssetAssignmentPanel from "../components/asset/AssetAssignmentPanel"
import { moduleColumns } from "@hydrologiq/shared/asset/PaginatedTable"
import Link from "@hydrologiq/shared/components/Link"
import { ActionButton } from "@hydrologiq/shared/content/components/Button"
import LinkAssetDialog from "../components/asset/LinkAssetDialog"
import type { GetAssetQuery } from "@hydrologiq/gql-experience/graphql"

type AssetDetail = GetAssetQuery["asset"]

const Asset = () => {
  const { assetId = "" } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get("tab")
  const { asset, refetch } = useAsset(assetId) as { asset: AssetDetail | undefined; refetch: () => void }
  const { deployments, loading: deploymentsLoading } = useAssetDeployments(assetId)

  // Fetch parent asset name when viewing a child asset
  const { asset: parentAsset } = useAsset(asset?.parentId ?? undefined)
  const parentName = parentAsset?.name ?? null

  // Check if deployments section is enabled for this tenant
  const deploymentsEnabled = useIsSectionEnabled("deployments")
  const [linkDialogOpen, setLinkDialogOpen] = useState(false)

  // Derive system ID from the active assignment (if any)
  const activeAssignment = asset?.assignments?.all?.find((a) => a.active)
  const systemId = activeAssignment?.system ?? undefined

  // Can link if: is not itself a child and grouping feature is on
  const canLink = !asset?.parentId

  const copy = useCopyValues({
    [COPY_KEYS.HEADINGS.ORDERS]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.ORDERS],
  })

  // Show Modules tab for hybrid assets, electrolysers/stacks, complex assets, or assets with existing children
  const assetClassStr = (asset?.class || (asset as any)?.class_field)?.toLowerCase()
  const assetKindStr = asset?.kind?.toLowerCase()
  const assetNameStr = asset?.name?.toLowerCase() ?? ""

  const isHybrid = assetClassStr === "hybrid"
  const isElectrolyser = assetClassStr === "electrolyser" || assetKindStr === "electrolyser" || assetNameStr.includes("electrolyser")
  const isStack = assetNameStr.includes("stack")
  const isComplex = assetKindStr === "complex"

  const showModulesTab = Boolean(
    asset &&
      !asset.parentId &&
      (asset.isParent || isHybrid || isElectrolyser || isStack || isComplex)
  )

  const tabDefaultValue = useMemo(
    () => (showModulesTab ? "modules" : asset?.parentId ? "information" : "assignments"),
    [asset, showModulesTab],
  )

  const currentTab = useMemo(() => {
    if (tabParam === "modules" && showModulesTab) return "modules"
    if (tabParam === "assignments") return "assignments"
    if (tabParam === "information") return "information"
    if (tabParam === "orders" && deploymentsEnabled) return "orders"
    return tabDefaultValue
  }, [tabParam, showModulesTab, deploymentsEnabled, tabDefaultValue])

  return (
    <SectionRoot>
      <Section>
        <AssetFigures asset={asset} onMonitoringStatusChange={() => refetch()} />
      </Section>
      <Tabs.Root
        value={currentTab}
        onValueChange={(e) => setSearchParams({ tab: e.value }, { replace: true })}
        variant={"sectionLozenge" as any}
      >
        <Tabs.List>
          {showModulesTab && (
            <Tabs.Trigger value="modules">Modules</Tabs.Trigger>
          )}
          <Tabs.Trigger value="assignments">Assignments</Tabs.Trigger>
          <Tabs.Trigger value="information">Information</Tabs.Trigger>
          {deploymentsEnabled && (
            <Tabs.Trigger value="orders">{copy[COPY_KEYS.HEADINGS.ORDERS]}</Tabs.Trigger>
          )}
        </Tabs.List>

        {showModulesTab && (
          <Tabs.Content value="modules">
            <PaginatedTable
              data={asset?.children ?? []}
              columns={moduleColumns}
              headerActions={
                <ActionButton
                  context={{ label: "Link assets", description: "Link additional assets as modules", variant: "secondary" }}
                  onClick={() => setLinkDialogOpen(true)}
                />
              }
              emptyState={{
                heading: "No modules",
                text: "This asset has no linked modules.",
              }}
            />
          </Tabs.Content>
        )}

        <Tabs.Content value="assignments">
          {asset?.parentId ? (
              <Text fontSize="sm">
                <b>Assignment management is disabled</b> for this module asset. Please manage assignment within the {" "}
                <Link href={`/assets/${asset.parentId}`} variant="primary" external={true}>
                  {parentName || "parent asset"}
                </Link>
              </Text>
          ) : (
            <AssetAssignmentPanel asset={asset} refetch={refetch} />
          )}
        </Tabs.Content>

        <Tabs.Content value="information" style={{ borderTopLeftRadius: 0 }}>
          <VStack gap={"12px"}>
            <AssetInformation
              asset={asset}
              refetch={() => setTimeout(() => refetch(), 100)}
              key={`${asset?.id}-${asset?.kind}`}
            />
            <AssetGraphics asset={asset} />
          </VStack>
        </Tabs.Content>

        {deploymentsEnabled && (
          <Tabs.Content value="orders">
            <DeploymentsTable deployments={deployments} loading={deploymentsLoading} asset={asset?.id} />
          </Tabs.Content>
        )}
      </Tabs.Root>

      {/* Link dialog — rendered outside tabs so it can be triggered from Information or Modules */}
      {canLink && (
        <LinkAssetDialog
          isOpen={linkDialogOpen}
          onClose={() => setLinkDialogOpen(false)}
          assetId={assetId}
          assetClass={asset?.class ?? null}
          systemId={systemId}
          onLinked={() => {
            setLinkDialogOpen(false)
            refetch()
          }}
        />
      )}
    </SectionRoot>
  )
}

export default Asset
